"use client"

import * as React from "react"
import { type DateRange } from "react-day-picker"
import { ChevronDown } from "lucide-react"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Calendar } from "@/components/ui/calendar"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import { ru } from "date-fns/locale"
import { getPeriodRange } from "@/lib/period-utils"

export type AnalyticsPeriod = 'today' | 'yesterday' | 'week' | 'month' | 'quarter' | 'custom'

export interface CustomDateRange {
  from: Date
  to: Date
}

export interface PeriodSettings {
  period: AnalyticsPeriod
  includeAdmins: boolean
  customRange?: CustomDateRange
}

interface AnalyticsPeriodSelectorProps {
  period: AnalyticsPeriod
  includeAdmins: boolean
  customRange?: CustomDateRange
  onPeriodChange: (period: AnalyticsPeriod, customRange?: CustomDateRange) => void
  onIncludeAdminsChange: (includeAdmins: boolean) => void
  /** Скрыть чекбокс «С учётом администраторов» (по умолчанию true = показать) */
  showIncludeAdmins?: boolean
  /** Дополнительный контент справа (вместо или вместе с чекбоксом) */
  rightSlot?: React.ReactNode
}

// Функция для форматирования даты на русском языке
function formatDateRu(date: Date): string {
  const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']
  const day = date.getDate()
  const month = months[date.getMonth()]
  return `${day} ${month}`
}

// Функция для форматирования даты в формат ДД.ММ.ГГГГ
function formatDateInput(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

// Функция для парсинга даты из формата ДД.ММ.ГГГГ
function parseDateInput(input: string): Date | null {
  const match = input.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
  if (!match) return null
  
  const day = parseInt(match[1], 10)
  const month = parseInt(match[2], 10)
  const year = parseInt(match[3], 10)
  
  // Проверка корректности даты
  if (month < 1 || month > 12) return null
  if (day < 1 || day > 31) return null
  
  const date = new Date(year, month - 1, day)
  
  // Проверка что дата действительно существует (не 31 февраля)
  if (date.getDate() !== day || date.getMonth() !== month - 1 || date.getFullYear() !== year) {
    return null
  }
  
  return date
}

// Функция для форматирования диапазона дат
function formatDateRange(from: Date, to: Date): string {
  const fromStr = formatDateRu(from)
  const toStr = formatDateRu(to)
  
  // Если даты в одном месяце и году
  if (from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()) {
    if (from.getDate() === to.getDate()) {
      return fromStr
    }
    return `${from.getDate()} - ${to.getDate()} ${formatDateRu(to).split(' ')[1]}`
  }
  
  return `${fromStr} - ${toStr}`
}

// Функция для автоформатирования при вводе
function formatInputAsTyping(value: string): string {
  // Убираем все кроме цифр
  const digits = value.replace(/\D/g, '')
  
  // Ограничиваем 8 цифрами
  const limited = digits.slice(0, 8)
  
  // Форматируем
  if (limited.length <= 2) return limited
  if (limited.length <= 4) return limited.slice(0, 2) + '.' + limited.slice(2)
  return limited.slice(0, 2) + '.' + limited.slice(2, 4) + '.' + limited.slice(4)
}

// Склонение слов для дней
function getDaysWord(count: number): string {
  if (count % 10 === 1 && count % 100 !== 11) return 'день'
  if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) return 'дня'
  return 'дней'
}

// Склонение слов для месяцев
function getMonthsWord(count: number): string {
  if (count % 10 === 1 && count % 100 !== 11) return 'месяц'
  if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) return 'месяца'
  return 'месяцев'
}

// Функция для расчета длительности периода
function calculatePeriodDuration(from: Date, to: Date): string {
  const diffMs = to.getTime() - from.getTime()
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1 // +1 включает оба дня
  
  if (days === 1) return '1 день'
  if (days < 31) return `${days} ${getDaysWord(days)}`
  
  const months = Math.floor(days / 30)
  const remainingDays = days % 30
  
  if (remainingDays === 0) {
    return `${months} ${getMonthsWord(months)}`
  }
  
  return `${months} ${getMonthsWord(months)} ${remainingDays} ${getDaysWord(remainingDays)}`
}

// Функция для получения дат из предустановленных периодов
function getPeriodDates(period: AnalyticsPeriod, customRange?: CustomDateRange): { from: Date; to: Date } | null {
  if (period === 'custom' && customRange) {
    return { from: customRange.from, to: customRange.to }
  }
  
  if (period === 'custom') {
    return null
  }
  
  const range = getPeriodRange(period)
  return { from: range.startDate, to: range.endDate }
}

export function AnalyticsPeriodSelector({ 
  period, 
  includeAdmins,
  customRange,
  onPeriodChange, 
  onIncludeAdminsChange,
  showIncludeAdmins = true,
  rightSlot,
}: AnalyticsPeriodSelectorProps) {
  const [dateRange, setDateRange] = React.useState<DateRange | undefined>(
    customRange ? { from: customRange.from, to: customRange.to } : undefined
  )
  const [open, setOpen] = React.useState(false)
  const [fromInput, setFromInput] = React.useState('')
  const [toInput, setToInput] = React.useState('')
  const [fromTouched, setFromTouched] = React.useState(false)
  const [toTouched, setToTouched] = React.useState(false)

  // Инициализация инпутов при открытии попапа
  React.useEffect(() => {
    if (open) {
      const dates = getPeriodDates(period, customRange)
      if (dates) {
        setDateRange({ from: dates.from, to: dates.to })
        setFromInput(formatDateInput(dates.from))
        setToInput(formatDateInput(dates.to))
      } else {
        // Если нет дат, используем текущий месяц
        const today = new Date()
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
        setDateRange({ from: startOfMonth, to: today })
        setFromInput(formatDateInput(startOfMonth))
        setToInput(formatDateInput(today))
      }
      // Сбрасываем touched при открытии
      setFromTouched(false)
      setToTouched(false)
    }
  }, [open, period, customRange])

  // Синхронизация инпутов при изменении dateRange из календаря
  React.useEffect(() => {
    if (dateRange?.from) {
      setFromInput(formatDateInput(dateRange.from))
    }
    if (dateRange?.to) {
      setToInput(formatDateInput(dateRange.to))
    }
  }, [dateRange])

  const handlePeriodChange = (value: string) => {
    if (value !== 'custom') {
      onPeriodChange(value as AnalyticsPeriod)
    }
  }

  const handleDateSelect = (range: DateRange | undefined) => {
    setDateRange(range)
    // Убрали автозакрытие - оно будет только при нажатии "Применить"
  }

  const handleFromInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const formatted = formatInputAsTyping(value)
    setFromInput(formatted)
    
    const parsed = parseDateInput(formatted)
    if (parsed && dateRange?.to) {
      setDateRange({ from: parsed, to: dateRange.to })
    } else if (parsed) {
      setDateRange({ from: parsed, to: undefined })
    }
  }

  const handleToInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const formatted = formatInputAsTyping(value)
    setToInput(formatted)
    
    const parsed = parseDateInput(formatted)
    if (parsed && dateRange?.from) {
      setDateRange({ from: dateRange.from, to: parsed })
    } else if (parsed) {
      setDateRange({ from: undefined, to: parsed })
    }
  }

  // Обработчик потери фокуса для автокоррекции порядка дат
  const handleFromInputBlur = () => {
    setFromTouched(true)
    const fromDate = parseDateInput(fromInput)
    const toDate = parseDateInput(toInput)
    
    // Если обе даты валидны и порядок неправильный
    if (fromDate && toDate && fromDate > toDate) {
      // Меняем местами
      setFromInput(formatDateInput(toDate))
      setToInput(formatDateInput(fromDate))
      setDateRange({ from: toDate, to: fromDate })
    }
  }

  const handleToInputBlur = () => {
    setToTouched(true)
    const fromDate = parseDateInput(fromInput)
    const toDate = parseDateInput(toInput)
    
    // Если обе даты валидны и порядок неправильный
    if (fromDate && toDate && fromDate > toDate) {
      // Меняем местами
      setFromInput(formatDateInput(toDate))
      setToInput(formatDateInput(fromDate))
      setDateRange({ from: toDate, to: fromDate })
    }
  }

  // Валидация
  const isValid = React.useMemo(() => {
    const fromDate = parseDateInput(fromInput)
    const toDate = parseDateInput(toInput)
    
    if (!fromDate || !toDate) return false
    
    const today = new Date()
    today.setHours(23, 59, 59, 999) // Конец сегодняшнего дня
    
    // Проверка на будущие даты
    if (fromDate > today || toDate > today) return false
    
    // Проверка порядка (уже исправляется при onBlur, но для кнопки)
    return fromDate <= toDate
  }, [fromInput, toInput])

  const handleApply = () => {
    if (isValid && dateRange?.from && dateRange?.to) {
      onPeriodChange('custom', { from: dateRange.from, to: dateRange.to })
      setOpen(false)
    }
  }

  // Обработка клавиши Escape
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        setOpen(false)
      }
    }
    
    if (open) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  // Текст для кнопки выбора периода - всегда показываем даты
  const customPeriodLabel = React.useMemo(() => {
    const dates = getPeriodDates(period, customRange)
    if (dates) {
      return formatDateRange(dates.from, dates.to)
    }
    return 'Период'
  }, [period, customRange])

  return (
    <div className="sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 z-10 border-b border-border/50 px-4 lg:px-6 py-3">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
        
        <Tabs value={period === 'custom' ? '' : period} onValueChange={handlePeriodChange}>
          <TabsList className="w-auto">
            <TabsTrigger value="today">Сегодня</TabsTrigger>
            <TabsTrigger value="yesterday">Вчера</TabsTrigger>
            <TabsTrigger value="week">Неделя</TabsTrigger>
            <TabsTrigger value="month">Месяц</TabsTrigger>
            <TabsTrigger value="quarter">Квартал</TabsTrigger>
            
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <button
                  className={`data-[state=active]:bg-background dark:data-[state=active]:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30 text-foreground dark:text-muted-foreground inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 ${
                    period === 'custom' ? 'bg-background' : ''
                  }`}
                  data-state={period === 'custom' ? 'active' : 'inactive'}
                >
                  {customPeriodLabel}
                  <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <div className="flex flex-col">
                  <Calendar
                    mode="range"
                    defaultMonth={dateRange?.from}
                    selected={dateRange}
                    onSelect={handleDateSelect}
                    disabled={(date) => date > new Date()}
                    numberOfMonths={2}
                    locale={ru}
                    className="rounded-lg"
                  />
                  
                  <Separator />
                  
                  <div className="flex items-center justify-between gap-2 p-3">
                    <div className="flex items-center gap-2">
                      <Input 
                        value={fromInput}
                        onChange={handleFromInputChange}
                        onFocus={(e) => e.target.select()}
                        onBlur={handleFromInputBlur}
                        placeholder="ДД.ММ.ГГГГ"
                        className="w-32 h-8"
                        aria-invalid={fromTouched && (() => {
                          const parsed = parseDateInput(fromInput)
                          return !parsed || parsed > new Date()
                        })()}
                      />
                      <span className="text-muted-foreground">-</span>
                      <Input 
                        value={toInput}
                        onChange={handleToInputChange}
                        onFocus={(e) => e.target.select()}
                        onBlur={handleToInputBlur}
                        placeholder="ДД.ММ.ГГГГ"
                        className="w-32 h-8"
                        aria-invalid={toTouched && (() => {
                          const parsed = parseDateInput(toInput)
                          return !parsed || parsed > new Date()
                        })()}
                      />
                      {dateRange?.from && dateRange?.to && (
                        <span className="text-sm text-muted-foreground whitespace-nowrap">
                          {calculatePeriodDuration(dateRange.from, dateRange.to)}
                        </span>
                      )}
                    </div>
                    <Button 
                      onClick={handleApply}
                      disabled={!isValid}
                      size="sm"
                    >
                      Применить
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </TabsList>
        </Tabs>
        
        <div className="flex items-center gap-3">
          {showIncludeAdmins && (
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="include-admins" 
                checked={includeAdmins}
                onCheckedChange={(checked) => onIncludeAdminsChange(!!checked)}
              />
              <Label 
                htmlFor="include-admins" 
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                С учетом администраторов
              </Label>
            </div>
          )}
          {rightSlot}
        </div>
      </div>
    </div>
  )
}
